import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { Dispatch } from 'redux';
import { frontloadConnect } from 'react-frontload';
import { PageTopicAggregation, PageTopicAggregationProps } from 'components/pages/PageTaxonomyAggregation';
import {
  userShortcutsUrlsSelector,
  userRolesSelector,
  isAuthSelector,
  userEmailSelector,
  userFVSSectionMemberSelector,
  userOktaIdSelector,
} from 'modules/user/selectors';
import {
  aggsPageContentSelector,
  contentLoadingSelector,
  topicContentListSelector,
  skipContentFrontloadRequestSelector,
} from 'modules/content/selectors';
import { fetchAggsPageContent, resetSkipContentFrontloadRequest } from 'modules/content/actions';
import { topicsHashSelector, topicsSlugIdMapSelector } from 'modules/topics/selectors';
import {
  topicFromParamsSelectorFactory,
  subtopicFromParamsSelectorFactory,
  pageTopicAggsTopicSlugSelector,
  pageTopicAggsSubtopicSlugSelector,
} from 'modules/router';
import { emptyObject, emptyArray, getPath } from 'utils';
import { Routes } from 'constants/index';
import { toggleLoginReload } from 'modules/layouts';

const mapActionCreators = (dispatch: Dispatch) => ({
  async fetchAggsPageContent(): Promise<void> {
    await dispatch(fetchAggsPageContent()).catch(() => dispatch(push(getPath(Routes.NOT_FOUND))));
  },

  toggleLoginReload(shouldReload: boolean): void {
    dispatch(toggleLoginReload(shouldReload));
  },
  resetSkipContentFrontloadRequest(): void {
    dispatch(resetSkipContentFrontloadRequest());
  },
});

const mapStateToProps = (initState: State.Root) => {
  const topicFromParamsSelector = topicFromParamsSelectorFactory(pageTopicAggsTopicSlugSelector);
  const subtopicFromParamsSelector = subtopicFromParamsSelectorFactory(pageTopicAggsSubtopicSlugSelector);

  const subtopicsFromParamsSelector = createSelector(
    [topicsHashSelector, topicsSlugIdMapSelector, pageTopicAggsTopicSlugSelector],
    (
      topicsHash: State.TopicsHash | null,
      topicsSlugIdMap: { [key: string]: string },
      slug: string | null
    ): State.Subtopic[] => {
      if (!slug || !topicsHash || !topicsSlugIdMap) return emptyArray;
      const topicId: string = topicsSlugIdMap[slug];
      if (!topicId) return emptyArray;
      const subtopics: State.SubtopicsHash = topicsHash[topicId].subtopics;
      return Object.values(subtopics || emptyObject);
    }
  );

  const userHasShortcutSelector = createSelector(
    [userShortcutsUrlsSelector, pageTopicAggsTopicSlugSelector, pageTopicAggsSubtopicSlugSelector],
    (userShortcutsUrls: string[], topicSlug: string, subtopicSlug: string): boolean => {
      if (!userShortcutsUrls || !userShortcutsUrls.length || !topicSlug) return false;
      return userShortcutsUrls.includes(`${topicSlug}${subtopicSlug}`);
    }
  );

  const userRoles: string[] | undefined = userRolesSelector(initState);
  const userRolesStr: string = userRoles ? userRoles.join() : '';

  return (state: State.Root) => {
    const topicSlug: string = pageTopicAggsTopicSlugSelector(state);
    const subtopicSlug: string = pageTopicAggsSubtopicSlugSelector(state);
    const isSubtopicPage: boolean = Boolean(subtopicSlug);

    const topic: State.Topic | null = topicFromParamsSelector(state);
    const subtopic: State.Subtopic | null = subtopicFromParamsSelector(state);
    const { id: topicId, name: topicName = '', description: topicDescription = '' }: any = topic || emptyObject;
    const {
      id: subtopicId,
      name: subtopicName = '',
      description: subtopicDescription = '',
    }: any = subtopic || emptyObject;

    return {
      loading: contentLoadingSelector(state),
      isSubtopicPage,
      topicSlug,
      topicId,
      topicName,
      topicDescription,
      subtopicSlug,
      subtopicId,
      subtopicName,
      subtopicDescription,
      subtopics: subtopicsFromParamsSelector(state),
      aggsPageContent: aggsPageContentSelector(state),
      hasShortcut: userHasShortcutSelector(state),
      userRolesStr,
      isAuth: isAuthSelector(state),
      topicContentList: topicContentListSelector(state),
      userEmail: userEmailSelector(state),
      fvsMembership: userFVSSectionMemberSelector(state),
      oktaId: userOktaIdSelector(state),
      skipContentFrontloadRequest: skipContentFrontloadRequestSelector(state),
    };
  };
};

const frontload = async (props: PageTopicAggregationProps) => {
  if (props.skipContentFrontloadRequest) return props.resetSkipContentFrontloadRequest();
  if (props.topicSlug) {
    return props.fetchAggsPageContent();
  }
};

const options = {
  onUpdate: true,
  _experimental_updateFunc: (prevProps: PageTopicAggregationProps, newProps: PageTopicAggregationProps) => {
    return (
      prevProps.isSubtopicPage !== newProps.isSubtopicPage ||
      prevProps.topicSlug !== newProps.topicSlug ||
      prevProps.subtopicSlug !== newProps.subtopicSlug ||
      prevProps.isAuth !== newProps.isAuth
    );
  },
};

export const PageTopicAggregationContainer = connect(
  mapStateToProps,
  mapActionCreators
)(frontloadConnect(frontload, options)(PageTopicAggregation));
